import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import { getSettings, updateSettings } from '../Utils/settingsApi';

const EMPTY_PAGE = { title: '', subtitle: '', content: '' };

function parsePage(raw) {
    if (!raw) return EMPTY_PAGE;
    try {
        const parsed = JSON.parse(raw);
        return {
            title: parsed.title || '',
            subtitle: parsed.subtitle || '',
            content: parsed.content || '',
        };
    } catch {
        return EMPTY_PAGE;
    }
}

/**
 * Éditeur générique (Titre/Sous-titre/Contenu) pour un document légal stocké
 * dans le magasin de réglages clé/valeur — settingKey peut être "legal_notice",
 * "terms_of_sale", ou "terms_of_use:<client_id>" pour les CGU d'un logiciel.
 */
export default function LegalPageEditor({ settingKey, heading, description, backTo, backLabel }) {
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const canEdit = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('settings.legal-content');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const [page, setPage] = useState(EMPTY_PAGE);

    const fetchPage = useCallback(() => {
        if (!accessToken) return;
        setLoading(true);
        getSettings(accessToken)
            .then((data) => setPage(parsePage(data[settingKey])))
            .catch(() => setError('Impossible de charger le contenu'))
            .finally(() => setLoading(false));
    }, [accessToken, settingKey]);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const handleSave = () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        updateSettings(accessToken, { [settingKey]: JSON.stringify(page) })
            .then(() => setSaved(true))
            .catch(() => setError('Échec de l\'enregistrement'))
            .finally(() => setSaving(false));
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(backTo)}
                sx={{ mb: 2 }}
            >
                {backLabel}
            </Button>

            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 3, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {heading}
            </Typography>

            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
                {description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {description}
                    </Typography>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Contenu enregistré.</Alert>}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
                ) : (
                    <Stack spacing={3}>
                        {!canEdit && (
                            <Alert severity="info">Lecture seule — la permission "Éditer CGU / CGV / mentions légales" est requise pour modifier ce contenu.</Alert>
                        )}
                        <TextField
                            label="Titre"
                            value={page.title}
                            onChange={(e) => setPage({ ...page, title: e.target.value })}
                            size="small"
                            fullWidth
                            disabled={!canEdit}
                        />
                        <TextField
                            label="Sous-titre"
                            value={page.subtitle}
                            onChange={(e) => setPage({ ...page, subtitle: e.target.value })}
                            size="small"
                            fullWidth
                            disabled={!canEdit}
                        />
                        <TextField
                            label="Contenu"
                            value={page.content}
                            onChange={(e) => setPage({ ...page, content: e.target.value })}
                            size="small"
                            fullWidth
                            multiline
                            minRows={12}
                            disabled={!canEdit}
                        />
                        {canEdit && (
                            <Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                                </Button>
                            </Box>
                        )}
                    </Stack>
                )}
            </Paper>
        </Box>
    );
}
